import React, {
  Suspense,
  useMemo,
  useRef,
  useCallback,
  useState,
} from 'react';
import { Canvas } from '@react-three/fiber';
import {
  CameraControls,
  Environment,
  AccumulativeShadows,
  RandomizedLight,
  Grid,
  useGLTF,
  Html,
  GizmoHelper,
  GizmoViewport,
  Outlines,
  Bvh,
  useCursor,
} from '@react-three/drei';
import * as THREE from 'three';
import { R1AS_ROOM_LABELS } from '../../data/r1asRoomLabels';
import {
  catalogGlbUrl,
  DEFAULT_SITE_PLANNER_MODEL,
} from '../../data/sitePlannerCatalog';

/** SoftPlan inches → meters */
const INCH_TO_M = 1 / 39.37;

const TARGET = [0, 2.2, 0];

const VIEW_PRESETS = {
  iso: [12, 7, 14],
  front: [0, 4.5, 16],
  side: [16, 4.5, 0],
  top: [0, 22, 0.05],
};

/**
 * R3F / drei Construx model viewer — docs/R3F_VIEWER.md
 * @param {{ modelUrl?: string, className?: string, heightClass?: string, showLabels?: boolean }} props
 */
function R3FModelViewer({
  modelUrl = catalogGlbUrl(DEFAULT_SITE_PLANNER_MODEL),
  className = '',
  heightClass = 'h-[min(80vh,720px)]',
  showLabels = true,
}) {
  const controlsRef = useRef(null);
  const [labelsOn, setLabelsOn] = useState(showLabels);
  const [selectedName, setSelectedName] = useState(null);

  const goToView = useCallback((key) => {
    const controls = controlsRef.current;
    if (!controls) return;
    const eye = VIEW_PRESETS[key] || VIEW_PRESETS.iso;
    const [tx, ty, tz] = TARGET;
    controls.setLookAt(eye[0], eye[1], eye[2], tx, ty, tz, true);
  }, []);

  const toolBtn =
    'px-2.5 py-1 text-xs md:text-sm rounded-[10px] border border-black/10 bg-white/90 hover:bg-white backdrop-blur-sm transition-colors font-bai';
  const toolBtnOn = `${toolBtn} bg-gray-900 text-white border-gray-900 hover:bg-gray-800`;

  return (
    <div
      className={`relative w-full ${heightClass} rounded-xl overflow-hidden bg-[#d9d4cc] ${className}`}
    >
      <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-1.5">
        <button type="button" className={toolBtn} onClick={() => goToView('iso')}>
          Iso
        </button>
        <button type="button" className={toolBtn} onClick={() => goToView('front')}>
          Front
        </button>
        <button type="button" className={toolBtn} onClick={() => goToView('side')}>
          Side
        </button>
        <button type="button" className={toolBtn} onClick={() => goToView('top')}>
          Top
        </button>
        <button type="button" className={toolBtn} onClick={() => goToView('iso')}>
          Reset
        </button>
        <button
          type="button"
          className={labelsOn ? toolBtnOn : toolBtn}
          onClick={() => setLabelsOn((v) => !v)}
          aria-pressed={labelsOn}
        >
          Labels
        </button>
      </div>

      <div className="absolute bottom-3 left-3 z-10 max-w-[70%] pointer-events-none">
        <p className="text-[11px] text-gray-600 bg-white/70 px-2 py-1 rounded font-bai">
          Drag orbit · scroll zoom · right-drag pan · click mesh to select
        </p>
        {selectedName && (
          <p className="mt-1 text-[11px] text-gray-800 bg-white/85 px-2 py-1 rounded font-bai">
            Selected: {selectedName}
          </p>
        )}
      </div>

      <Canvas
        shadows
        dpr={[1, 1.75]}
        camera={{ position: VIEW_PRESETS.iso, fov: 40, near: 0.05, far: 500 }}
        gl={{ antialias: true, toneMapping: THREE.ACESFilmicToneMapping }}
        onPointerMissed={() => setSelectedName(null)}
      >
        <color attach="background" args={['#d9d4cc']} />
        <ambientLight intensity={0.42} />
        <directionalLight intensity={0.35} position={[-8, 12, -6]} />

        <Suspense fallback={<LoaderNote />}>
          <Bvh firstHitOnly>
            <SceneModel url={modelUrl} onSelectName={setSelectedName} />
          </Bvh>
          {labelsOn && <RoomLabels />}
          <Environment preset="warehouse" />
          <AccumulativeShadows
            temporal
            frames={48}
            color="#3d3832"
            opacity={0.55}
            scale={36}
            position={[0, 0.01, 0]}
            alphaTest={0.75}
          >
            <RandomizedLight
              amount={6}
              radius={9}
              ambient={0.45}
              intensity={1.1}
              position={[14, 20, 10]}
              bias={0.001}
            />
          </AccumulativeShadows>
        </Suspense>

        <Grid
          position={[0, -0.001, 0]}
          args={[40, 40]}
          cellSize={0.5}
          cellThickness={0.55}
          cellColor="#b5aea3"
          sectionSize={2.5}
          sectionThickness={1.05}
          sectionColor="#8a8278"
          fadeDistance={36}
          fadeStrength={1.15}
          infiniteGrid
        />
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, -0.012, 0]}
          receiveShadow
        >
          <planeGeometry args={[80, 80]} />
          <meshStandardMaterial color="#cdc6bc" roughness={0.95} metalness={0} />
        </mesh>

        <CameraControls
          ref={controlsRef}
          makeDefault
          minDistance={3}
          maxDistance={80}
          minPolarAngle={0.15}
          maxPolarAngle={Math.PI * 0.49}
          dollySpeed={0.85}
          truckSpeed={1.2}
          smoothTime={0.35}
        />

        <GizmoHelper alignment="bottom-right" margin={[72, 72]}>
          <GizmoViewport
            axisColors={['#c45c3e', '#5a8f5a', '#4a6fa5']}
            labelColor="white"
          />
        </GizmoHelper>
      </Canvas>
    </div>
  );
}

function LoaderNote() {
  return (
    <Html center>
      <p className="font-bai text-sm text-gray-700 bg-white/80 px-3 py-2 rounded shadow-sm">
        Loading R1AS…
      </p>
    </Html>
  );
}

function RoomLabels() {
  return (
    <group>
      {R1AS_ROOM_LABELS.map((room) => (
        <Html
          key={room.name}
          position={room.position}
          center
          distanceFactor={14}
          zIndexRange={[20, 0]}
          style={{ pointerEvents: 'none' }}
        >
          <div className="font-bai text-[10px] md:text-xs tracking-wide uppercase px-2 py-0.5 rounded-sm bg-gray-900/80 text-white whitespace-nowrap shadow-sm">
            {room.name}
          </div>
        </Html>
      ))}
    </group>
  );
}

function SceneModel({ url, onSelectName }) {
  const { scene } = useGLTF(url);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  useCursor(Boolean(hovered));

  const meshes = useMemo(() => {
    const root = scene.clone(true);
    const box = new THREE.Box3().setFromObject(root);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    root.position.x -= center.x;
    root.position.y -= center.y;
    root.position.z -= center.z;
    root.position.y += size.y / 2;
    root.updateMatrixWorld(true);

    const list = [];
    root.traverse((obj) => {
      if (!obj.isMesh) return;
      obj.castShadow = true;
      obj.receiveShadow = true;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m) => {
        if (!m) return;
        m.side = THREE.DoubleSide;
        m.needsUpdate = true;
      });

      const pos = new THREE.Vector3();
      const quat = new THREE.Quaternion();
      const scl = new THREE.Vector3();
      obj.matrixWorld.decompose(pos, quat, scl);
      list.push({
        uuid: obj.uuid,
        geometry: obj.geometry,
        material: obj.material,
        position: pos.toArray(),
        quaternion: [quat.x, quat.y, quat.z, quat.w],
        scale: scl.toArray(),
        name: obj.name || obj.material?.name || `Part ${list.length + 1}`,
      });
    });
    return list;
  }, [scene]);

  return (
    <group scale={INCH_TO_M}>
      {meshes.map((m) => {
        const active = selected === m.uuid;
        const isHover = hovered === m.uuid;
        return (
          <mesh
            key={m.uuid}
            geometry={m.geometry}
            material={m.material}
            position={m.position}
            quaternion={m.quaternion}
            scale={m.scale}
            castShadow
            receiveShadow
            onClick={(e) => {
              e.stopPropagation();
              setSelected(m.uuid);
              onSelectName?.(m.name);
            }}
            onPointerOver={(e) => {
              e.stopPropagation();
              setHovered(m.uuid);
            }}
            onPointerOut={() => setHovered(null)}
          >
            {(active || isHover) && (
              <Outlines
                thickness={active ? 3.5 : 2}
                color={active ? '#c45c3e' : '#4a6fa5'}
                screenspace
                opacity={1}
              />
            )}
          </mesh>
        );
      })}
    </group>
  );
}

useGLTF.preload(catalogGlbUrl(DEFAULT_SITE_PLANNER_MODEL));

export default R3FModelViewer;
